/**
 * API Endpoints Configuration
 * 
 * This is the central file containing all API endpoints for the Interview-Copilot application.
 * It includes endpoints for the Python FastAPI backend, LiveKit services, WebSockets, and other services.
 */

// Determine current environment
const currentEnv = process.env.NODE_ENV || 'development';

// Fetch URLs from environment variables with fallbacks
const config = {
  apiBaseUrl: process.env.REACT_APP_PYTHON_API_BASE_URL || '',
  livekitUrl: process.env.REACT_APP_LIVEKIT_URL || '',
  websocketUrl: process.env.REACT_APP_WEBSOCKETS_URL || '',
};

/**
 * Central API Endpoints Configuration
 */
export const APIEndpoints = {
  // Base URLs
  
  BASE_URL: config.apiBaseUrl,
  LIVEKIT_URL: config.livekitUrl,
  WEBSOCKET_URL: config.websocketUrl,
  
  // Server Status
  STATUS: `${config.apiBaseUrl}/`,
  DEBUG_USER_CONTEXTS: `${config.apiBaseUrl}/debug/user-contexts`,
  
  // LiveKit Integration
  LIVEKIT: {
    TOKEN: `${config.apiBaseUrl}/get-livekit-token`,
    WEBHOOK: `${config.apiBaseUrl}/livekit-webhook`,
    SERVER_URL: config.livekitUrl,
  },
  
  // WebSocket Endpoints
  WEBSOCKET: {
    TRANSCRIPTIONS: `${config.websocketUrl}/ws/transcriptions`,
  },
  
  // User Management
  USER: {
    PREFERENCES: `${config.apiBaseUrl}/user-preferences`,
  },
  
  // Session Management
  SESSION: {
    CREATE: `${config.apiBaseUrl}/sessions`,
    GET_BY_ID: (sessionId: string) => `${config.apiBaseUrl}/sessions/${sessionId}`,
    END: (sessionId: string) => `${config.apiBaseUrl}/sessions/${sessionId}/end`,
    CLOSE: (sessionId: string) => `${config.apiBaseUrl}/session/${sessionId}/close`,
    TRANSCRIPTIONS: (sessionId: string) => `${config.apiBaseUrl}/sessions/${sessionId}/transcriptions`,
  },
  
  // AI Services
  AI: {
    RESPONSE: `${config.apiBaseUrl}/ai/response`,
    FEEDBACK: `${config.apiBaseUrl}/ai/feedback`,
  },
  
  // Chatbot Services
  CHATBOT: {
    QUERY: `${config.apiBaseUrl}/chatbot/query`,
  },
  
  // User Session Management
  USER_SESSION: {
    CLEAR: (userId: string) => `${config.apiBaseUrl}/user/${userId}/session`,
    UPDATE_ID: (userId: string) => `${config.apiBaseUrl}/user/${userId}/session-id`,
  },
  
  // Question Bank
  QUESTIONS: {
    LIST: `${config.apiBaseUrl}/questions`,
    GET_BY_ID: (questionId: string) => `${config.apiBaseUrl}/questions/${questionId}`,
  },
  
  // Prompts Management
  PROMPTS: {
    // Addon Prompts (category="addon")
    ADDONS: {
      LIST: (forceRefresh: boolean = false) => `${config.apiBaseUrl}/api/prompts/addons?force_refresh=${forceRefresh}`,
      GET_BY_TITLE: (title: string) => `${config.apiBaseUrl}/api/prompts/addons/${encodeURIComponent(title)}`,
    },
    
    // Admin endpoints
    ADMIN: {
      ADD: `${config.apiBaseUrl}/admin/prompts/add`,
      ADD_ADDON: `${config.apiBaseUrl}/admin/prompts/addons/add`,
      CACHE_STATUS: `${config.apiBaseUrl}/admin/prompts/cache/status`,
      CACHE_REFRESH: `${config.apiBaseUrl}/admin/prompts/cache/refresh`,
      CACHE_CLEAR: `${config.apiBaseUrl}/admin/prompts/cache/clear`,
      VERSION_CHECKER_INTERVAL: `${config.apiBaseUrl}/admin/prompts/version-checker/interval`,
    },
  },
};

/**
 * HTTP Headers Configuration
 */
export const Headers = {
  // Default headers for API requests
  DEFAULT: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  
  // Authentication headers factory
  getAuthHeaders: (token?: string) => {
    if (!token) return Headers.DEFAULT;
    
    return {
      ...Headers.DEFAULT,
      'Authorization': `Bearer ${token}`
    };
  },
  
  // Multipart form headers (for file uploads)
  MULTIPART: {
    'Accept': 'application/json',
  }
};

/**
 * Environment Information
 */
export const Environment = {
  current: currentEnv,
  isDevelopment: currentEnv === 'development',
  isProduction: currentEnv === 'production',
  isTest: currentEnv === 'test',
};

const config_export = {
  APIEndpoints,
  Headers,
  Environment,
};

export default config_export;
